import { Button, Form, Input, Modal } from 'antd';
import { useContext } from 'react';

import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useUpdateReview } from 'app/hooks/data/core/admin/reviews/useUpdateReview';
import type { BookingReviewResponse } from 'app/typings/core/admin/bookings/booking';
import type { ReviewFormRequest } from 'app/typings/core/admin/bookings/bookingForm';

import styles from './Modals.module.scss';

const { TextArea } = Input;

type Props = {
  reviewDetails?: BookingReviewResponse;
  isEditReviewModalVisible: boolean;
  setIsEditReviewModalVisible: (isVisible: boolean) => void;
  bookingCode: string;
};

export const EditReview = ({
  isEditReviewModalVisible,
  setIsEditReviewModalVisible,
  reviewDetails,
  bookingCode,
}: Props) => {
  const [form] = Form.useForm();
  const { enterViewMode } = useContext(ViewEditModeContext);
  const reviewValue = Form.useWatch('comment', form);

  const { mutateAsync: saveReview } = useUpdateReview();

  const onClose = () => {
    setIsEditReviewModalVisible(false);
    enterViewMode();
  };

  const onSubmit = async (values: ReviewFormRequest) => {
    if (!reviewDetails) {
      return;
    }

    await saveReview({
      reviewId: reviewDetails.id,
      payload: values,
      bookingCode,
    });
    onClose();
  };

  const initialValues = { comment: reviewDetails?.comment || '' };
  const isSaveDisabled = !reviewValue || reviewValue === reviewDetails?.comment;

  return (
    <Modal
      width={572}
      onCancel={onClose}
      open={isEditReviewModalVisible}
      title="Edit review"
      centered
      className={styles.editReviewModal}
      wrapClassName={styles.editReviewModalWrapper}
      footer={[
        <Button
          type="primary"
          onClick={() => form.submit()}
          disabled={isSaveDisabled}
          key="save"
        >
          Save
        </Button>,
      ]}
    >
      <Form form={form} onFinish={onSubmit} initialValues={initialValues}>
        <FormLineLayout
          label="Review"
          isRequired
          rightClassName={styles.reviewField}
          labelOnTop
        >
          <Form.Item
            name="comment"
            rules={[{ required: true, message: 'Please enter a review' }]}
            noStyle
          >
            <TextArea />
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {() =>
              form.getFieldError('comment').length > 0 && (
                <div className={styles.errorMessage}>
                  {form.getFieldError('comment')[0]}
                </div>
              )
            }
          </Form.Item>
        </FormLineLayout>
      </Form>
    </Modal>
  );
};
